import React, { FunctionComponent, MouseEvent, ReactNode } from 'react';

import { useParams } from '@reach/router';
import { useEthers } from '@usedapp/core';

import Button from '@bitcoin-portal/verse-web-components/dist/Button';
import H3 from '@bitcoin-portal/verse-web-components/dist/H3';
import Add from '@bitcoin-portal/verse-web-components/dist/Icons/Add';
import Search from '@bitcoin-portal/verse-web-components/dist/Icons/Search';
import Link from '@bitcoin-portal/verse-web-components/dist/Link';
import { colors } from '@bitcoin-portal/verse-web-components/dist/themes/colors';

import { useTrackedState } from '../../context/store';

import { chainIdToExplorer, logLpDepositSuccessCtaEvent } from '../../helpers';

import { AnimationWrapper, Body, Details, Header, ViewWrapper } from './styled';

interface TxSuccessProps {
  title: string;
  loaderAnimation: ReactNode;
  subtitle: string | ReactNode;
  description?: string;
  lpTokenAddress?: string;
  transactionHash?: string;
  customButton?: ReactNode;
  destinationChain?: number;
}

export const TxSuccess: FunctionComponent<TxSuccessProps> = ({
  title,
  loaderAnimation,
  subtitle,
  description,
  lpTokenAddress,
  transactionHash,
  customButton,
  destinationChain,
}) => {
  const { selectedLiquidityPair } = useTrackedState();
  const { chainId } = useEthers();
  const explorer = chainIdToExplorer(destinationChain || chainId);
  const { chain } = useParams() || {
    chain: 'eth',
    pair: '',
  };

  const tickerA = selectedLiquidityPair.token0?.symbol || '';
  const tickerB = selectedLiquidityPair.token1?.symbol || '';

  return (
    <ViewWrapper>
      <Header>
        <H3 style={{ padding: '16px 0 8px' }}>{title}</H3>
      </Header>
      <AnimationWrapper>{loaderAnimation}</AnimationWrapper>
      <Details>
        <H3 style={{ marginTop: 32 }}>{subtitle}</H3>
        <Body
          style={{
            marginTop: 8,
            color: colors.shade90,
            marginBottom: 32,
          }}
        >
          {description}
        </Body>
        {customButton || (
          <Link href="/pools/" style={{ width: '100%' }}>
            <Button as="div" fullWidth>
              View All Pools
            </Button>
          </Link>
        )}
        {lpTokenAddress && (
          <Button
            design="clear"
            style={{ marginTop: 25, height: 'unset' }}
            onClick={async (e: MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              logLpDepositSuccessCtaEvent({
                action: 'add token to metamask',
                blockchain: chain.toUpperCase(),
              });
              if (window && typeof window.ethereum !== 'undefined') {
                await window.ethereum.request({
                  method: 'wallet_watchAsset',
                  params: {
                    type: 'ERC20',
                    options: {
                      address: lpTokenAddress,
                      symbol: 'VERSE-X',
                      decimals: 18,
                    },
                  },
                });
              }
            }}
          >
            <Add
              size={15}
              color={colors.verseBlue}
              style={{ marginRight: 5 }}
            />
            Add VERSE-X ({tickerA} / {tickerB}) to MetaMask
          </Button>
        )}
        {transactionHash && (
          <Link
            href={`https://${explorer}/tx/${transactionHash}`}
            newTab
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              if (lpTokenAddress) {
                logLpDepositSuccessCtaEvent({
                  action: 'view transaction',
                  blockchain: chain.toUpperCase(),
                });
              }
            }}
            style={{
              marginTop: 27,
              display: 'flex',
              fontSize: 14,
            }}
          >
            <Search
              size={13}
              color={colors.verseBlue}
              style={{
                marginRight: 7,
                alignSelf: 'center',
              }}
            />
            View Transaction
          </Link>
        )}
      </Details>
    </ViewWrapper>
  );
};

export default TxSuccess;
